import React, { useCallback, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import { ResizeBorder } from '../ResizeBorder';
import { RESIZE_THUMB_POSITIONS } from '../../../constants';

const round = (value, resizeSteps) => Math.ceil(value / resizeSteps) * resizeSteps;

const RESIZE_OPTIONS = {
    AUTO: 'auto',
    RELATIVE: 'relative',
    ABSOLUTE: 'absolute'
};

const ResizableBlock = ({
    blockProps,
    className,
    resizeSteps = 1,
    config,
    component: WrappedComponent,
    getEditorRef,
    isReadOnly,
    ...elementProps
}) => {
    const {
        vertical = RESIZE_OPTIONS.ABSOLUTE,
        horizontal = RESIZE_OPTIONS.ABSOLUTE,
        initialWidth,
        initialHeight
    } = config;
    const [currentWidth, setCurrentWidth] = useState(initialWidth);
    const [currentHeight, setCurrentHeight] = useState(initialHeight);
    const wrapperRef = useRef();
    const isFocused = className.includes('onsolve-wysiwyg-editor__block--focused');

    const pointerDown = useCallback(
        (event, { current: handlerRef }, handlerPosition) => {
            event.persist();
            event.preventDefault();
            handlerRef.setPointerCapture(event.pointerId);

            const hoverPosition = getHoverPosition(handlerPosition);

            const startX = event.clientX;
            const startY = event.clientY;
            const startWidth = parseInt(getComputedStyle(wrapperRef.current).width, 10);
            const startHeight = parseInt(getComputedStyle(wrapperRef.current).height, 10);

            const handleDragStart = dragEvent => {
                dragEvent.preventDefault();

                const { width: newWidth, height: newHeight } = calculateBlockDimensions({
                    hoverPosition,
                    startWidth,
                    startHeight,
                    startX,
                    startY,
                    currentX: dragEvent.clientX,
                    currentY: dragEvent.clientY
                });

                setCurrentWidth(newWidth || startWidth);
                setCurrentHeight(newHeight || startHeight);
            };

            const handleDragStop = () => {
                handlerRef.releasePointerCapture(event.pointerId);
                handlerRef.removeEventListener('pointermove', handleDragStart);
                handlerRef.removeEventListener('pointerup', handleDragStop);
            };

            handlerRef.addEventListener('pointermove', handleDragStart);
            handlerRef.addEventListener('pointerup', handleDragStop);
        },
        [calculateBlockDimensions]
    );

    const getHoverPosition = handlerPosition => {
        return {
            isTop: [
                RESIZE_THUMB_POSITIONS.TOP,
                RESIZE_THUMB_POSITIONS.TOP_LEFT,
                RESIZE_THUMB_POSITIONS.TOP_RIGHT
            ].includes(handlerPosition),
            isLeft: [
                RESIZE_THUMB_POSITIONS.LEFT,
                RESIZE_THUMB_POSITIONS.TOP_LEFT,
                RESIZE_THUMB_POSITIONS.BOTTOM_LEFT
            ].includes(handlerPosition),
            isRight: [
                RESIZE_THUMB_POSITIONS.RIGHT,
                RESIZE_THUMB_POSITIONS.TOP_RIGHT,
                RESIZE_THUMB_POSITIONS.BOTTOM_RIGHT
            ].includes(handlerPosition),
            isBottom: [
                RESIZE_THUMB_POSITIONS.BOTTOM,
                RESIZE_THUMB_POSITIONS.BOTTOM_LEFT,
                RESIZE_THUMB_POSITIONS.BOTTOM_RIGHT
            ].includes(handlerPosition)
        };
    };

    const calculateBlockDimensions = useCallback(
        ({ hoverPosition, startWidth, startHeight, startX, startY, currentX, currentY }) => {
            let width;
            let height;

            const isHorizontalMove = hoverPosition.isLeft || hoverPosition.isRight;
            const isVerticalMove = hoverPosition.isTop || hoverPosition.isBottom;

            const editorComponent = getEditorRef().editor;

            if (isHorizontalMove) {
                width = calculateBlockSideLength({
                    reverseDirection: hoverPosition.isLeft,
                    resizeOption: horizontal,
                    initialSize: startWidth,
                    initialCoords: startX,
                    currentCoords: currentX,
                    editorSideLength: editorComponent.clientWidth
                });
            }

            if (isVerticalMove) {
                height = calculateBlockSideLength({
                    reverseDirection: hoverPosition.isTop,
                    resizeOption: vertical,
                    initialSize: startHeight,
                    initialCoords: startY,
                    currentCoords: currentY,
                    editorSideLength: editorComponent.clientHeight
                });
            }

            return { width, height };
        },
        [getEditorRef, horizontal, vertical, calculateBlockSideLength]
    );

    const calculateBlockSideLength = useCallback(
        ({ reverseDirection, resizeOption, initialSize, initialCoords, currentCoords, editorSideLength }) => {
            const delta = currentCoords - initialCoords;
            let size = Math.min(editorSideLength, reverseDirection ? initialSize - delta : initialSize + delta);

            if (resizeOption === RESIZE_OPTIONS.RELATIVE) {
                size = (100 / editorSideLength) * size;
            }

            return resizeSteps ? round(size, resizeSteps) : size;
        },
        [resizeSteps]
    );

    const getBlockSizeStyle = useCallback(({ resizeOption, size }) => {
        if (resizeOption === RESIZE_OPTIONS.AUTO) {
            return RESIZE_OPTIONS.AUTO;
        }

        const sizeUnits = resizeOption === RESIZE_OPTIONS.RELATIVE ? '%' : 'px';

        return `${size}${sizeUnits}`;
    }, []);

    const wrappedComponentStyles = useMemo(() => {
        const styles = { position: 'relative' };

        styles.width = getBlockSizeStyle({
            resizeOption: horizontal,
            size: currentWidth
        });
        styles.height = getBlockSizeStyle({
            resizeOption: vertical,
            size: currentHeight
        });

        return styles;
    }, [horizontal, currentHeight, vertical, currentWidth, getBlockSizeStyle]);

    const interactionProps = isReadOnly || !isFocused ? {} : { onPointerDown: pointerDown };

    return (
        <ResizeBorder isVisible={isFocused} ref={wrapperRef} block={elementProps.block} {...interactionProps}>
            <WrappedComponent
                {...elementProps}
                className={className}
                blockProps={blockProps}
                style={wrappedComponentStyles}
                indentable={false}
            />
        </ResizeBorder>
    );
};

ResizableBlock.propTypes = {
    blockProps: PropTypes.object,
    className: PropTypes.string,
    component: PropTypes.elementType.isRequired,
    config: PropTypes.shape({
        initialWidth: PropTypes.string,
        initialHeight: PropTypes.string,
        horizontal: PropTypes.oneOf(Object.values(RESIZE_OPTIONS)),
        vertical: PropTypes.oneOf(Object.values(RESIZE_OPTIONS))
    }),
    getEditorRef: PropTypes.func.isRequired,
    isReadOnly: PropTypes.bool,
    resizeSteps: PropTypes.number
};

ResizableBlock.displayName = `ResizableBlock`;

export default ResizableBlock;
